import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { retrySyncAll } from '@/services/admin/sync-notifications';

/**
 * BD 17.0 全て再同期 — one call for the whole inbox, whatever page the operator is on.
 *
 * BD 17.0 (2026-08-14) settled the toast at a single sentence: the list is refetched right after the press, so
 * the real state of every record is on screen already and the skipped count has nothing left to tell. The
 * earlier two-number sentence 「同期済みのレセプト〇件を除外し…」 is gone — `excluded`/`dispatched` stay in the
 * response as data, not as message copy.
 */
export const useRetrySyncMutation = () => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteType, category }: { siteType: string; category: string }) =>
      retrySyncAll(siteType, category),
    onSuccess: async response => {
      const { remaining } = response.data;

      toast.success('同期が完了しました。');

      // Capped presses are the norm on a real backlog (retry_max_records). Saying so turns 「the list did not
      // empty」 from a bug report into one more press.
      if (remaining > 0) {
        toast.info(`未同期のレセプトが${remaining}件残っています。もう一度実行してください。`);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sync-notifications'] }),
        queryClient.invalidateQueries({ queryKey: ['sync-notifications-count'] }),
      ]);
    },
    onError: error => {
      const status = (error as any)?.response?.status;
      if (status !== 401) {
        toast.error('エラーが発生しました。');
      }
    },
  });
};
